/**
 * Shared layout primitives for the HKEJ v2 main site.
 *
 * The 860px content + 300px sidebar two-column grid (with a 40px gutter) is the
 * canonical main-site segment layout. It was copy-pasted — often as local
 * `SEGMENT_GRID` / `SEGMENT_GAP` consts — across finnews, columns, tags,
 * live-news, features, search and all five property pages. Import these instead.
 *
 * Usage:
 *   <div className={SEGMENT_GRID} style={SEGMENT_GAP}> … </div>
 */
export const SEGMENT_GRID =
  "grid grid-cols-1 lg:[grid-template-columns:minmax(0,860px)_minmax(0,300px)]";

export const SEGMENT_GAP = { columnGap: 40, rowGap: 40 } as const;

/** Standard centered page container: 1200px max-width, mobile gutter, flush on lg. */
export const PAGE_CONTAINER = "mx-auto max-w-[1440px] px-4 lg:px-[120px]";

/* -------------------------------------------------------------------------- */
/* Sidebar tablet-reflow grid                                                 */
/* -------------------------------------------------------------------------- */
/**
 * On tablet the single-column sidebar widget item-lists reflow into a
 * multi-column grid; on mobile (below the band) and desktop (lg+) they stay a
 * one-item-per-row vertical list. Below `lg` the sidebar already spans full
 * content width (see SEGMENT_GRID), so the grid always has room.
 *
 * `SIDEBAR_TABLET_BP` is the single switch that moves the tablet-start between
 * `sm` (640px) and `md` (768px). Flip this one constant to change the band for
 * every widget that consumes the constants below.
 *
 * NOTE (Tailwind v4 JIT): the scanner only generates classes that appear as
 * complete string literals in scanned files. Every class string below is a full
 * literal for BOTH bands — the ternary only selects which literal is returned,
 * so flipping `SIDEBAR_TABLET_BP` needs no new strings and both bands stay
 * generated. Never interpolate the breakpoint token or a gap value into a class.
 *
 * Widgets with bespoke in-cell band logic hardcode their breakpoints to the
 * `sm` decision and must be re-audited by hand if this switch is ever flipped
 * (see plan R3). `SIDEBAR_VIDEO_CARD` no longer among them: it left the grid
 * for the tablet carousel below, which is band-aware.
 */
export type SidebarTabletBP = "sm" | "md";
export const SIDEBAR_TABLET_BP: SidebarTabletBP = "sm";

const SM = SIDEBAR_TABLET_BP === "sm";

/** 3-up grid, gap-x 24 / gap-y 32, with a 20px flex-axis gap on mobile/desktop. */
export const SIDEBAR_LIST_GRID_3_GAP20 = SM
  ? "flex flex-col gap-[20px] sm:max-lg:grid sm:max-lg:grid-cols-3 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full"
  : "flex flex-col gap-[20px] md:max-lg:grid md:max-lg:grid-cols-3 md:max-lg:gap-x-0 md:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full";

/** 3-up grid, gap-x 24 / gap-y 32, no flex-axis gap (rows carry own spacing). */
export const SIDEBAR_LIST_GRID_3 = SM
  ? "flex flex-col sm:max-lg:grid sm:max-lg:grid-cols-3 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[20px] lg:flex lg:flex-col w-full"
  : "flex flex-col md:max-lg:grid md:max-lg:grid-cols-3 md:max-lg:gap-x-0 md:max-lg:gap-y-[20px] lg:flex lg:flex-col w-full";

/** 2-up grid, gap-x 24 / gap-y 32, with a 20px flex-axis gap on mobile/desktop. */
export const SIDEBAR_LIST_GRID_2_GAP20 = SM
  ? "flex flex-col gap-[20px] sm:max-lg:grid sm:max-lg:grid-cols-2 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full"
  : "flex flex-col gap-[20px] md:max-lg:grid md:max-lg:grid-cols-2 md:max-lg:gap-x-0 md:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full";

/**
 * 4-up grid, gap-x 24 / gap-y 32, with a 20px flex-axis gap on mobile/desktop.
 * Currently unused — `SidebarVideoCard` was its only consumer and now uses the
 * tablet snap carousel instead. Kept as the 4-up member of the set.
 */
export const SIDEBAR_LIST_GRID_4_GAP20 = SM
  ? "flex flex-col gap-[20px] sm:max-lg:grid sm:max-lg:grid-cols-4 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full"
  : "flex flex-col gap-[20px] md:max-lg:grid md:max-lg:grid-cols-4 md:max-lg:gap-x-0 md:max-lg:gap-y-[20px] lg:flex lg:flex-col lg:gap-[20px] w-full";

/** 2-up grid, gap-x 24 / gap-y 32. */
export const SIDEBAR_LIST_GRID_2 = SM
  ? "flex flex-col sm:max-lg:grid sm:max-lg:grid-cols-2 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[20px] lg:flex lg:flex-col w-full"
  : "flex flex-col md:max-lg:grid md:max-lg:grid-cols-2 md:max-lg:gap-x-0 md:max-lg:gap-y-[20px] lg:flex lg:flex-col w-full";

/** 2-up grid, gap-x 24 / gap-y 16. */
export const SIDEBAR_LIST_GRID_2_GAPY16 = SM
  ? "flex flex-col sm:max-lg:grid sm:max-lg:grid-cols-2 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[16px] lg:flex lg:flex-col w-full"
  : "flex flex-col md:max-lg:grid md:max-lg:grid-cols-2 md:max-lg:gap-x-0 md:max-lg:gap-y-[16px] lg:flex lg:flex-col w-full";

/**
 * 2-up grid with NO row gap.
 *
 * For ruled lists whose vertical rhythm comes from the items' own `py-*` plus
 * their `border-b`, not from the grid. Adding a row gap on top of that padding
 * makes tablet rows sit further apart than the desktop column does — 8 + 1 + 16
 * against 8 + 1 + 8 — which reads as uneven spacing. Pair it with items that
 * KEEP their top padding, i.e. do NOT apply `SIDEBAR_TABLET_GAP_RESET`, since
 * that reset exists precisely to stop `pt` stacking on a row gap.
 */
export const SIDEBAR_LIST_GRID_2_GAPY0 = SM
  ? "flex flex-col sm:max-lg:grid sm:max-lg:grid-cols-2 sm:max-lg:gap-x-0 sm:max-lg:gap-y-0 lg:flex lg:flex-col w-full"
  : "flex flex-col md:max-lg:grid md:max-lg:grid-cols-2 md:max-lg:gap-x-0 md:max-lg:gap-y-0 lg:flex lg:flex-col w-full";

/** 2-up grid, gap-x 24 / gap-y 12. */
export const SIDEBAR_LIST_GRID_2_GAPY12 = SM
  ? "flex flex-col sm:max-lg:grid sm:max-lg:grid-cols-2 sm:max-lg:gap-x-0 sm:max-lg:gap-y-[12px] lg:flex lg:flex-col w-full"
  : "flex flex-col md:max-lg:grid md:max-lg:grid-cols-2 md:max-lg:gap-x-0 md:max-lg:gap-y-[12px] lg:flex lg:flex-col w-full";

/**
 * Tablet keeps each row's bottom separator (border + bottom padding) so the
 * grid reads as a divided list, exactly like the mobile/desktop columns. This
 * constant only neutralizes a row's EXTRA inter-row spacing (top padding and
 * vertical margins) so it doesn't stack on top of the grid's row-gap. Apply it
 * to any grid row that uses pt / mt / mb for separation; rows that separate via
 * bottom padding alone need no reset. Each gridded row should draw its
 * `border-b` (and bottom padding) at ALL breakpoints — do not gate the divider
 * to mobile/desktop only, or the tablet grid loses its separators.
 *
 * The tablet grids use column-gap 0 (per-row column spacing lives inside each
 * cell via `tabletCellX`), so adjacent cells' bottom borders join into a SINGLE
 * continuous separator between rows — not an underline under each cell. The
 * first/last column's outer padding is 0 (see `tabletCellX`), so the joined
 * separator ends exactly at the component's content edges (no bleed). Cells in
 * the last grid row drop their border (see `isLastTabletRow`) so a partial/last
 * row shows no stray segment.
 */
export const SIDEBAR_TABLET_GAP_RESET = SM
  ? "sm:max-lg:pt-0 sm:max-lg:mt-0 sm:max-lg:mb-0"
  : "md:max-lg:pt-0 md:max-lg:mt-0 md:max-lg:mb-0";

/**
 * Per-cell horizontal padding for the tablet ruled grid (column-gap is 0 so the
 * joined `border-b` reads as one continuous line). The 24px column gutter lives
 * inside the cells, but the OUTER edge of the first/last column gets 0 padding
 * so the joined separator ends exactly at the component's content edges (no
 * bleed) and the item content aligns with the start/end of the component.
 * `cols` must match the widget's grid constant; `index` is the item's flow
 * position. Apply the result to the SAME element that carries the `border-b`.
 */
export function tabletCellX(index: number, cols: number): string {
  const col = index % cols;
  if (col === 0) return SM ? "sm:max-lg:pr-[12px]" : "md:max-lg:pr-[12px]";
  if (col === cols - 1)
    return SM ? "sm:max-lg:pl-[12px]" : "md:max-lg:pl-[12px]";
  return SM ? "sm:max-lg:px-[12px]" : "md:max-lg:px-[12px]";
}

/**
 * True when item `index` sits in the LAST row of the tablet `cols`-column grid.
 * Such cells get `sm:max-lg:border-b-0` so the last/partial row shows no stray
 * separator (there's no row below it); full rows above keep their full-width
 * joined border. `cols` must match the grid constant used by the widget.
 */
export function isLastTabletRow(
  index: number,
  total: number,
  cols: number,
): boolean {
  return index >= total - (total % cols || cols);
}

/**
 * The class the comment above prescribes for a last-tablet-row cell.
 *
 * It had never been written, so `sidebar-video-card.tsx` and
 * `latest-columns-card.tsx` both computed `isLastTabletRow(...)` / `isLast`,
 * passed them down, and then discarded them — every row drew `border-b`
 * unconditionally. QA saw the result on `/columns/news` at tablet: a stray
 * separator under the final row, sitting directly above the card's own CTA.
 *
 * Pair with `SIDEBAR_STACK_LAST_NO_BORDER`: the two cover different layouts of
 * the same widget, and the last item in the flow is usually not the same item
 * as the last tablet row.
 */
export const SIDEBAR_TABLET_LAST_ROW_NO_BORDER = SM
  ? "sm:max-lg:border-b-0"
  : "md:max-lg:border-b-0";

/**
 * Drops the divider under the FINAL item in the single-column layouts — mobile
 * (below the tablet breakpoint) and the lg sidebar — where the grid collapses
 * back to a flex column and the last row's border has nothing beneath it.
 */
export const SIDEBAR_STACK_LAST_NO_BORDER = SM
  ? "max-sm:border-b-0 lg:border-b-0"
  : "max-md:border-b-0 lg:border-b-0";

/* In-cell reorder fragments for the column/author row (text below, avatar
 * above) when a row is squeezed into a narrow grid cell. */
export const SIDEBAR_TABLET_STACK = SM
  ? "sm:max-lg:flex-col sm:max-lg:gap-[8px]"
  : "md:max-lg:flex-col md:max-lg:gap-[8px]";

export const SIDEBAR_TABLET_ORDER_TEXT = SM
  ? "sm:max-lg:order-2 sm:max-lg:w-full"
  : "md:max-lg:order-2 md:max-lg:w-full";

export const SIDEBAR_TABLET_ORDER_MEDIA = SM
  ? "sm:max-lg:order-1"
  : "md:max-lg:order-1";

/* -------------------------------------------------------------------------- */
/* Tablet-band snap carousel                                                  */
/* -------------------------------------------------------------------------- */
/**
 * A widget whose item list is too rich to squeeze into the tablet grid becomes
 * a swipeable row instead (see `TabletSnapCarousel`). Four 16:9 video cards in
 * a 4-up grid were ~170px wide on a 768 tablet and still left the card's CTA
 * alone on a full-width row underneath. QA (`/columns/news`, tablet):
 * 「平板用這個組件明顯不是一個好嘅選擇 建議使用可以左右滑動嘅組件，如果需要讓
 * 用戶查看更加多嘅視頻可以放在右上角而不是左下角導致大量空間留白」.
 *
 * These live here, next to the grid constants, rather than in the (client)
 * carousel component: a `"use client"` module's exports reach a server
 * component as client-reference stubs, so a class string exported from one
 * silently stringifies into `function(){throw Error(...)}` in `className`.
 *
 * As above, every string is a complete literal for BOTH bands.
 */

/** The scroller: a plain column everywhere, a snap row on the tablet band. */
export const SIDEBAR_TABLET_SNAP_ROW = SM
  ? "flex w-full flex-col gap-[20px] sm:max-lg:flex-row sm:max-lg:flex-nowrap sm:max-lg:gap-[24px] sm:max-lg:overflow-x-auto sm:max-lg:snap-x sm:max-lg:snap-mandatory sm:max-lg:no-scrollbar"
  : "flex w-full flex-col gap-[20px] md:max-lg:flex-row md:max-lg:flex-nowrap md:max-lg:gap-[24px] md:max-lg:overflow-x-auto md:max-lg:snap-x md:max-lg:snap-mandatory md:max-lg:no-scrollbar";

/**
 * One carousel cell, sized so **3.15** of them fill the scrollport — the
 * fraction IS the affordance: a clipped fourth card is what says the row
 * continues. It has to agree with the row's `gap-x`, since 3.15 visible items
 * span exactly 3 gutters. Change one and change the other.
 */
export const SIDEBAR_TABLET_SNAP_ITEM = SM
  ? "sm:max-lg:w-[calc((100%_-_72px)/3.15)] sm:max-lg:shrink-0 sm:max-lg:snap-start"
  : "md:max-lg:w-[calc((100%_-_72px)/3.15)] md:max-lg:shrink-0 md:max-lg:snap-start";

/** Present only on the tablet band (the scroll arrows' wrapper). */
export const SIDEBAR_TABLET_ONLY = SM
  ? "hidden sm:max-lg:block"
  : "hidden md:max-lg:block";

/**
 * Drops a stacked row's divider on the tablet band. Cells sitting side by side
 * would read as underlined, not separated — and with the CTA moved up beside
 * the title there is nothing below the row to separate from.
 */
export const SIDEBAR_TABLET_NO_DIVIDER = SM
  ? "sm:max-lg:border-b-0 sm:max-lg:pb-0"
  : "md:max-lg:border-b-0 md:max-lg:pb-0";

/**
 * Card body that lets the tablet band lift the CTA onto the title's row without
 * duplicating it in the DOM: title and button wrap as row 1 (`justify-between`),
 * the list is `w-full` and so is pushed onto row 2. Source order stays
 * title → list → button — which is exactly what mobile and `lg` render — and
 * only the tablet band reorders, via the three `*_ORDER_*` constants below.
 */
export const SIDEBAR_TABLET_HEADER_ROW = SM
  ? "flex w-full flex-col gap-[20px] sm:max-lg:flex-row sm:max-lg:flex-wrap sm:max-lg:items-center sm:max-lg:justify-between sm:max-lg:gap-x-[16px] sm:max-lg:gap-y-[20px]"
  : "flex w-full flex-col gap-[20px] md:max-lg:flex-row md:max-lg:flex-wrap md:max-lg:items-center md:max-lg:justify-between md:max-lg:gap-x-[16px] md:max-lg:gap-y-[20px]";

/**
 * Same wrap at the 16px rhythm, for the cards that already used `gap={16}`.
 *
 * A second complete literal rather than an interpolated gap: Tailwind's JIT
 * only emits classes it can see as whole strings, and stacking `gap-[16px]`
 * onto `gap-[20px]` in one class list is resolved by stylesheet order, not by
 * the order they are written — so it is not a reliable override.
 */
export const SIDEBAR_TABLET_HEADER_ROW_GAP16 = SM
  ? "flex w-full flex-col gap-[16px] sm:max-lg:flex-row sm:max-lg:flex-wrap sm:max-lg:items-center sm:max-lg:justify-between sm:max-lg:gap-x-[16px] sm:max-lg:gap-y-[16px]"
  : "flex w-full flex-col gap-[16px] md:max-lg:flex-row md:max-lg:flex-wrap md:max-lg:items-center md:max-lg:justify-between md:max-lg:gap-x-[16px] md:max-lg:gap-y-[16px]";

export const SIDEBAR_TABLET_HEADER_TITLE = SM
  ? "sm:max-lg:order-1"
  : "md:max-lg:order-1";

export const SIDEBAR_TABLET_HEADER_CTA = SM
  ? "self-start sm:max-lg:order-2 sm:max-lg:shrink-0 sm:max-lg:self-auto"
  : "self-start md:max-lg:order-2 md:max-lg:shrink-0 md:max-lg:self-auto";

export const SIDEBAR_TABLET_HEADER_LIST = SM
  ? "w-full sm:max-lg:order-3"
  : "w-full md:max-lg:order-3";
